import React from 'react';
import { shape, string, number, func, oneOf } from 'prop-types';
import { Paper, IconButton, Tooltip, Typography, Box } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { Rnd } from 'react-rnd';
import { I18n } from 'utils/i18n';

function localizedRoutingItemTitle(itemName) {
  if (itemName === 'dni') {
    return I18n.t('HrBot.EditTemplate.SetupDocumentPalette.dni');
  }
  if (itemName === 'year') {
    return I18n.t('HrBot.EditTemplate.SetupDocumentPalette.extractYear');
  }
  if (itemName === 'month') {
    return I18n.t('HrBot.EditTemplate.SetupDocumentPalette.extractMonth');
  }
  if (itemName === 'day') {
    return I18n.t('HrBot.EditTemplate.SetupDocumentPalette.extractDay');
  }
  if (itemName === 'code' || itemName === 'id') {
    return I18n.t('HrBot.EditTemplate.SetupDocumentPalette.employeeCode');
  }
  return itemName;
}

/** Show extracted preview in the routing box when there is any non-empty text. */
function shouldShowExtractedPreview(_itemName, trimmed) {
  return trimmed.length > 0;
}

function routingNotFoundMessage(itemName) {
  if (itemName === 'dni') {
    return I18n.t('HrBot.EditTemplate.SetupDocument.routingNotFoundDni');
  }
  if (itemName === 'year') {
    return I18n.t('HrBot.EditTemplate.SetupDocument.routingNotFoundYear');
  }
  if (itemName === 'month') {
    return I18n.t('HrBot.EditTemplate.SetupDocument.routingNotFoundMonth');
  }
  if (itemName === 'day') {
    return I18n.t('HrBot.EditTemplate.SetupDocument.routingNotFoundDay');
  }
  if (itemName === 'code' || itemName === 'id') {
    return I18n.t('HrBot.EditTemplate.SetupDocument.routingNotFoundEmployeeCode');
  }
  return I18n.t('HrBot.EditTemplate.SetupDocument.routingNotFoundEmployeeCode');
}

/**
 * Draggable routing box for template.items (selector === 'box').
 * Coords use PDF viewport space (top-left origin, y downward) to match pdf-utils/textInBox.
 */
const DocumentRoutingItem = ({
  item,
  pdfDimensions,
  onUpdate,
  onDelete,
  extractedPreview,
  detectionStatus = 'pending',
}) => {
  const { coords, name } = item;
  if (!coords || coords.left == null || !pdfDimensions) {
    return null;
  }

  const trimmedExtract =
    typeof extractedPreview === 'string' ? extractedPreview.replace(/\s+/g, ' ').trim() : '';
  const hasExtracted = shouldShowExtractedPreview(name, trimmedExtract);
  const status = detectionStatus || 'pending';
  let displayText = localizedRoutingItemTitle(name);
  if (status === 'found' && hasExtracted) {
    displayText = trimmedExtract;
  } else if (status === 'missing') {
    displayText = routingNotFoundMessage(name);
  }

  const scale = pdfDimensions.width / pdfDimensions.originalWidth;
  const pdfW = coords.right - coords.left;
  const pdfH = coords.bottom - coords.top;
  const w = pdfW * scale;
  const h = pdfH * scale;
  const displayX = coords.left * scale;
  const displayY = coords.top * scale;

  return (
    <Rnd
      position={{ x: displayX, y: displayY }}
      size={{ width: w, height: h }}
      minWidth={24}
      minHeight={12}
      onDragStop={(e, d) => {
        // `react-rnd` can emit dragStop on mount/sync with no real move — applying it would
        // overwrite good PDF coords (green "found") with bogus values and flip the box to red.
        const idlePx = 2;
        if (Math.abs(d.x - displayX) <= idlePx && Math.abs(d.y - displayY) <= idlePx) {
          return;
        }
        // Do not use d.node dimensions — the draggable node can be shorter than the Rnd box
        // (e.g. Paper shrinking to label), which collapsed coords on every drag end.
        const left = d.x / scale;
        const top = d.y / scale;
        onUpdate(item.name, {
          coords: {
            left,
            top,
            right: left + pdfW,
            bottom: top + pdfH,
          },
        });
      }}
      onResizeStop={(e, direction, ref, delta, position) => {
        const nw = ref.offsetWidth / scale;
        const nh = ref.offsetHeight / scale;
        const left = position.x / scale;
        const top = position.y / scale;
        const idlePx = 2;
        if (
          Math.abs(position.x - displayX) <= idlePx &&
          Math.abs(position.y - displayY) <= idlePx &&
          Math.abs(ref.offsetWidth - w) <= idlePx &&
          Math.abs(ref.offsetHeight - h) <= idlePx
        ) {
          return;
        }
        onUpdate(item.name, {
          coords: {
            left,
            top,
            right: left + nw,
            bottom: top + nh,
          },
        });
      }}
      bounds="parent"
      enableResizing={{
        bottom: true,
        bottomLeft: true,
        bottomRight: true,
        left: true,
        right: true,
        top: true,
        topLeft: true,
        topRight: true,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          '&:hover .routing-item-actions': {
            opacity: 1,
          },
        }}
      >
        <Paper
          elevation={0}
          sx={theme => ({
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
            borderRadius: 1,
            userSelect: 'none',
            boxShadow: theme.shadows[2],
            backgroundColor: '#fff8e1',
            border: `2px dashed ${theme.palette.warning.dark || '#f9a825'}`,
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'move',
            p: 0.5,
            transition: theme.transitions.create(['background-color', 'border-color'], {
              duration: 200,
            }),
            ...(status === 'found' && {
              backgroundColor: '#e8f5e9',
              border: `2px dashed ${theme.palette.success.main}`,
            }),
            ...(status === 'missing' && {
              backgroundColor: '#ffebee',
              border: `2px dashed ${theme.palette.error.main}`,
            }),
          })}
        >
          <Typography
            noWrap={status !== 'missing'}
            title={displayText}
            sx={theme => ({
              fontSize: '0.75rem',
              fontWeight: 600,
              color: theme.palette.text.secondary,
              lineHeight: 1.2,
              textAlign: 'center',
              maxWidth: '100%',
              minWidth: 0,
              minHeight: 0,
              flexShrink: 1,
              ...(status === 'found' &&
                hasExtracted && {
                  fontWeight: 500,
                  color: theme.palette.success?.dark || theme.palette.success.main,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }),
              ...(status === 'missing' && {
                color: theme.palette.error?.dark || theme.palette.error.main,
                fontWeight: 600,
                whiteSpace: 'normal',
              }),
            })}
          >
            {displayText}
          </Typography>
        </Paper>
        <Box
          className="routing-item-actions"
          sx={theme => ({
            position: 'absolute',
            top: '-28px',
            right: 0,
            display: 'flex',
            gap: 0.5,
            backgroundColor: theme.palette.background.paper,
            p: 0.25,
            borderRadius: '4px',
            boxShadow: theme.shadows[2],
            opacity: 0,
            transition: 'opacity 0.2s',
          })}
        >
          <Tooltip title={I18n.t('Common.delete')}>
            <IconButton size="small" onClick={() => onDelete(item.name)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Rnd>
  );
};

DocumentRoutingItem.propTypes = {
  item: shape({
    name: string.isRequired,
    selector: string.isRequired,
    coords: shape({
      left: number.isRequired,
      top: number.isRequired,
      right: number.isRequired,
      bottom: number.isRequired,
    }).isRequired,
  }).isRequired,
  pdfDimensions: shape({
    originalWidth: number.isRequired,
    originalHeight: number.isRequired,
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
  onUpdate: func.isRequired,
  onDelete: func.isRequired,
  extractedPreview: string,
  detectionStatus: oneOf(['pending', 'found', 'missing']),
};

export default DocumentRoutingItem;
